import type { CalculationResult, CalculatorInput, FeeProfile } from './types';
import { determineSizeTier } from './tiers';
import {
  getAgedInventorySurchargeRange,
  getExpectedReturnsCost,
  getFulfillmentFee,
  getFulfillmentSurchargeFee,
  getInboundPlacementFee,
  getLowInventoryLevelFee,
  getReferralFee,
  getStorageFees,
  getVariableClosingFee
} from './fees';

export function calculate(input: CalculatorInput, profile: FeeProfile): CalculationResult {
  const sizeTier = determineSizeTier(profile, input.weight, input.length, input.width, input.height);
  const fulfillmentFee = getFulfillmentFee(profile, input.category, sizeTier, input.weight, input.price);
  const fulfillmentSurchargeFee = getFulfillmentSurchargeFee(profile, fulfillmentFee);
  const referralFee = getReferralFee(profile, input.category, input.price);
  const variableClosingFee = getVariableClosingFee(profile, input.category);
  const inboundPlacementFee = getInboundPlacementFee(
    profile,
    input.inboundPlacementMode,
    input.inboundPlacementManual,
    sizeTier
  );

  const storage = getStorageFees(profile, sizeTier, input.length, input.width, input.height);
  const returnsEstimate = getExpectedReturnsCost(profile, input.category, fulfillmentFee, referralFee);
  const lowInventoryLevelFee = getLowInventoryLevelFee(profile, sizeTier, input.weight);
  const aged = getAgedInventorySurchargeRange(profile, input.length, input.width, input.height);

  const landedCost = input.cogs + input.inbound + input.duties + input.prep;
  const totalCost =
    landedCost + referralFee + variableClosingFee + fulfillmentFee + fulfillmentSurchargeFee + inboundPlacementFee;
  const profitPerUnit = input.price - totalCost;
  const marginPct = input.price > 0 ? (profitPerUnit / input.price) * 100 : 0;
  const roiPct = landedCost > 0 ? (profitPerUnit / landedCost) * 100 : 0;
  const consistentFeesTotal =
    referralFee +
    variableClosingFee +
    fulfillmentFee +
    fulfillmentSurchargeFee +
    inboundPlacementFee +
    storage.offSeason +
    storage.peak;

  return {
    profitPerUnit,
    marginPct,
    roiPct,
    landedCost,
    referralFee,
    variableClosingFee,
    fulfillmentFee,
    fulfillmentSurchargeFee,
    inboundPlacementFee,
    storageOffSeason: storage.offSeason,
    storagePeakSeason: storage.peak,
    consistentFeesTotal,
    expectedReturnsCost: returnsEstimate.expectedCostPerUnit,
    singleReturnEvent: returnsEstimate.singleReturnCost,
    lowInventoryLevelFee,
    agedInventoryMin: aged.minMonthly,
    agedInventoryMax: aged.maxMonthly,
    sizeTier
  };
}
